import { useTranslation } from 'react-i18next';
import type { CajaReporteTotals, ReportFormat } from '@/application/metrics';
import { ExportButtons } from './ExportButtons';
import { MetricKpiCard } from './MetricKpiCard';
import { formatCurrency, formatInteger } from './metricFormatters';

interface CajaReporteBlockProps {
  data: CajaReporteTotals;
  currency?: string;
  /** Sin esto no se muestran los botones de descarga (p. ej. en el consolidado). */
  onExport?: (format: ReportFormat) => Promise<void>;
}

/**
 * Reporte de caja: totales del período y desglose por forma de pago con comisiones.
 *
 * La comisión de tarjeta es un **recargo** que se le suma al paciente: `Monto` guarda la
 * base sin comisión, así que el total cobrado es ingresos + comisiones. Por eso la
 * comisión se muestra aparte y se suma, en vez de descontarse del neto.
 */
export function CajaReporteBlock({ data, currency = 'ARS', onExport }: CajaReporteBlockProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const money = (value: number) => formatCurrency(value, locale, currency);

  return (
    <section className="bi-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-950 dark:text-white">{t('metrics.caja.report.title')}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('metrics.caja.report.description')}</p>
        </div>
        {onExport && <ExportButtons onExport={onExport} />}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <MetricKpiCard label={t('metrics.caja.report.gross')} value={money(data.ingresos)} tone="green" metricId="revenue" />
        <MetricKpiCard label={t('metrics.caja.expenses')} value={money(data.egresos)} tone="red" metricId="expense" />
        <MetricKpiCard label={t('metrics.caja.net')} value={money(data.neto)} tone={data.neto >= 0 ? 'blue' : 'amber'} metricId="net" />
        <MetricKpiCard label={t('metrics.caja.report.commissions')} value={money(data.comisiones)} tone="amber" />
        <MetricKpiCard label={t('metrics.caja.report.totalCharged')} value={money(data.totalCobrado)} tone="slate" />
      </div>

      <h3 className="mb-2 mt-4 text-sm font-semibold text-gray-700 dark:text-gray-200">
        {t('metrics.caja.report.byPaymentMethod')}
      </h3>

      {data.porFormaPago.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('metrics.caja.report.empty')}</p>
      ) : (
        <div className="bi-table-shell overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="border-b border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)] text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">{t('metrics.caja.detail.method')}</th>
                <th className="px-3 py-2 text-right font-semibold">{t('metrics.caja.income')}</th>
                <th className="px-3 py-2 text-right font-semibold">{t('metrics.caja.expenses')}</th>
                <th className="px-3 py-2 text-right font-semibold">{t('metrics.caja.report.commissions')}</th>
                <th className="px-3 py-2 text-right font-semibold">{t('metrics.caja.movements')}</th>
              </tr>
            </thead>
            <tbody>
              {data.porFormaPago.map((fila) => (
                <tr key={fila.formaPago ?? '__empty__'} className="border-b border-[var(--color-border-subtle)]">
                  <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                    {fila.formaPago ?? t('metrics.caja.report.noPaymentMethod')}
                  </td>
                  <td className="bi-num px-3 py-2 text-right text-teal-600 dark:text-teal-400">
                    {money(fila.ingresos)}
                  </td>
                  <td className="bi-num px-3 py-2 text-right text-red-600 dark:text-red-400">
                    {money(fila.egresos)}
                  </td>
                  <td className="bi-num px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                    {money(fila.comisiones)}
                  </td>
                  <td className="bi-num px-3 py-2 text-right text-gray-600 dark:text-gray-300">
                    {formatInteger(fila.movimientos, locale)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-[var(--color-border)] bg-[var(--color-surface-muted)] font-semibold">
              <tr>
                <td className="px-3 py-2 text-gray-800 dark:text-gray-200">{t('metrics.caja.report.total')}</td>
                <td className="bi-num px-3 py-2 text-right text-gray-900 dark:text-gray-100">{money(data.ingresos)}</td>
                <td className="bi-num px-3 py-2 text-right text-gray-900 dark:text-gray-100">{money(data.egresos)}</td>
                <td className="bi-num px-3 py-2 text-right text-gray-900 dark:text-gray-100">{money(data.comisiones)}</td>
                <td className="bi-num px-3 py-2 text-right text-gray-900 dark:text-gray-100">
                  {formatInteger(data.movimientos, locale)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
